import {FC, memo, useRef} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {api, capitalizeFirstLetter, hasValue, useT} from '@kcast-fe/core';
import {Box, Flex} from '@chakra-ui/react';
import {APP_VARS, ROUTES} from 'core';
import {useAuthStore} from 'stores';
import {
  Svg,
  useModal,
  useIsOpen,
  QuickMenu,
  EllipseButton,
  useClickOutside,
  IQuickMenuItem
} from 'ui-kit';
import {SearchInputBig} from '@kcast-fe/ui-kit';

interface IProps {
  query: string;
  updateQuery: (q: string) => void;
}

const MenuHeaderFC: FC<IProps> = ({query, updateQuery}) => {
  const {data: settings} = api.settings.useSettingsQuery();

  const {actions} = useAuthStore(useShallow((state) => state));

  const {t} = useT();
  const {openModal} = useModal();
  const search = useIsOpen();

  const searchRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(searchRef, () => {
    if (!query) {
      search.close();
    }
  });

  const menuItems: IQuickMenuItem<string>[] = [
    {
      id: 'dashboard',
      isExternal: true,
      name: t('labels.dashboard'),
      onClick: () => window.open(`https://dashboard.${document.location.host}`, '_blank')
    },
    {
      id: 'memory',
      name: t('labels.memory'),
      onClick: () => openModal(ROUTES.Modal.Memory)
    },
    {
      id: 'agentTasks',
      name: t('labels.agentTasks'),
      onClick: () => openModal(ROUTES.Modal.AgentTasks)
    },
    {
      id: 'personalization',
      name: t('common.personalization'),
      onClick: () => openModal(ROUTES.Modal.Personalization)
    },
    {
      id: 'connections',
      name: t('common.connections'),
      onClick: () => openModal(ROUTES.Modal.Connections)
    },
    {
      id: 'downloadChat',
      name: t('actions.downloadChat'),
      onClick: () => openModal(ROUTES.Modal.DownloadChat)
    },
    {
      id: 'signOut',
      name: t('actions.signOut'),
      onClick: () => actions.signOut().then()
    }
  ];

  if (!hasValue(settings?.assistant?.name)) {
    return null;
  }

  return (
    <Box ref={containerRef} p={'16px'} justifySelf={'flex-start'}>
      <Flex p={'4px 8px'} pos={'relative'} alignItems={'center'} justifyContent={'space-between'}>
        {/* SEARCH INPUT */}
        {search.isOpen && (
          <Box ref={searchRef} pos={'absolute'} top={0} left={0} right={0} zIndex={1}>
            <SearchInputBig
              value={query}
              onChange={updateQuery}
              onClear={() => {
                updateQuery('');
                search.close();
              }}
            />
          </Box>
        )}

        {/* MENU CONTROL */}
        <Box>
          <QuickMenu
            hideActive
            isTriggerArrow
            items={menuItems}
            container={containerRef}
            placement={'bottom-start'}
            trigger={
              <Flex gap={'8px'}>
                <Box overflow={'hidden'} borderRadius={'4px'}>
                  <Svg name={'Logo'} size={'s24'} />
                </Box>
                <Box>{capitalizeFirstLetter(settings?.assistant?.name)}</Box>
              </Flex>
            }
            footerSlot={
              <Box
                p={'8px 12px'}
                fontSize={'16px'}
                borderTop={'thin solid'}
                borderColor={'{colors.borderDefault}'}
                color={'{colors.textTertiary}'}
              >
                FE {APP_VARS.FE_VERSION}
              </Box>
            }
          />
        </Box>

        <EllipseButton
          icon={'Search'}
          size={'normal'}
          iconSize={'s24'}
          variant={'transparent'}
          onClick={search.open}
        />
      </Flex>
    </Box>
  );
};

export const MenuHeader = memo(MenuHeaderFC);
